import { useState, useEffect } from 'react';

function useAutoplayInView(videoRef, {
  threshold = 0.5,
  rootMargin = "0px",
  pauseWhenOutOfView = true,
  respectReducedMotion = true,
  pauseOnPageHide = true,
  retryOnPlayRejection = true
} = {}) {
  const [inView, setInView] = useState(false);
  const [canAutoplay, setCanAutoplay] = useState(true);
  useEffect(() => {
    if (!respectReducedMotion) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setCanAutoplay(!mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, [respectReducedMotion]);
  useEffect(() => {
    if (!pauseOnPageHide) return;
    const onVis = () => {
      const el = videoRef.current;
      if (!el) return;
      if (document.visibilityState !== "visible") el.pause();
      else if (inView && canAutoplay && el.paused) {
        el.play().catch(() => {
        });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [pauseOnPageHide, inView, canAutoplay, videoRef]);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold, rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, videoRef]);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const shouldPlay = inView && canAutoplay;
    if (shouldPlay) {
      el.play().catch(() => {
        if (!retryOnPlayRejection) return;
      });
    } else if (pauseWhenOutOfView) {
      el.pause();
    }
  }, [inView, canAutoplay, pauseWhenOutOfView, retryOnPlayRejection, videoRef]);
  return { inView, canAutoplay };
}

export { useAutoplayInView };
